//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateGeneralViewMode } from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import "../../Components/TemplateStringInput";
import "./WorkflowTemplateFilePatternView";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { showToastMessage, stringComparison } from "../../utils/app-utils";

/**
 * Workflow Template Workflow Information Tab
 *
 * This tab contains workflow configuration fields with variable support:
 * - Workflow Name Pattern
 * - Transfer Type
 * - Business Unit Participant
 * - Source Location Path (Push only; hidden for Pull, same as regular Workflow)
 * - Trigger File Patterns
 * - Data File Patterns
 */
@customElement("workflow-template-info-tab")
export class WorkflowTemplateInfoTab extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-info-tab";
  }

  @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
  @property({ type: String }) workflowTemplateId: string = "";
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: Object }) templateData: any = null; // Passed from parent - no need to fetch
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

  // Form Data
  @state() workflowName: string = "";
  @state() transferType: string = "";
  @state() sourceLocationPath: string = "";
  @state() triggerFilePatterns: string[] = [];
  @state() dataFilePatterns: string[] = [];

  // Validation States
  @state() isWorkflowNameValid: boolean = false;
  @state() workflowNameError: string = "";
  @state() isTransferTypeValid: boolean = false;
  @state() isSourceLocationPathValid: boolean = false;
  @state() sourceLocationPathError: string = "";

  // Loading State
  @state() apiLoading: boolean = false;

  // Dirty State Tracking - indicates unsaved changes
  @state() isDirty: boolean = false;

  // Mid-edit state for file patterns (input has value but not added to list)
  @state() private filePatternMidEdit: boolean = false;

  // Flag to prevent marking dirty during initial load/render
  private isInitializing: boolean = true;

  // Original values to compare against for dirty detection
  private originalData = {
    workflowName: "",
    transferType: "",
    sourceLocationPath: "",
    triggerFilePatterns: [] as string[],
    dataFilePatterns: [] as string[],
  };

  static readonly styles = css`
    :host {
      display: block;
      padding: 20px;
    }

    h2 {
      margin-top: 0;
      margin-bottom: 24px;
      font-size: 24px;
      font-weight: 600;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }

    #basicInfoContainer {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
      margin-bottom: 40px;
    }

    #basicInfoContainer > * {
      min-width: 0;
    }

    /* Responsive grid breakpoints */
    @media (max-width: 768px) {
      #basicInfoContainer {
        grid-template-columns: 1fr;
      }
    }

    #sourceLocationSection {
      margin-bottom: 40px;
    }

    #sourceLocationSection h2 {
      margin-bottom: 16px;
    }

    #sourceLocationSection template-string-input {
      width: 25vw;
    }

    #filePatternsSection {
      margin-bottom: 32px;
    }

    jha-form-select {
      width: 100%;
    }

    template-string-input {
      width: 100%;
    }

    workflow-template-file-pattern-view {
      width: 100%;
      display: block;
      --file-pattern-column-gap: 48px;
    }

    .full-width {
      grid-column: 1 / -1;
    }

  `;

  async connectedCallback() {
    super.connectedCallback();

    // Load template data if in EDIT or VIEW mode
    if (this.workflowTemplateId) {
      await this.loadTemplateData();
    } else {
      // ADD mode - new template starts clean (isDirty = false)
      // Dirty state is only set when user actually changes something via markDirty()
      this.isDirty = false;
    }

    this.validateForm();
  }

  /**
   * Called after the first render - safe to start tracking dirty state
   * and emit initial validation to parent
   */
  firstUpdated() {
    // Allow a brief delay for all @change events from initial render to complete
    setTimeout(() => {
      this.isInitializing = false;
      // Re-emit validation now that parent's event listeners are definitely attached
      this.validateForm();
    }, 100);
  }

  /**
   * Load workflow info data for EDIT/VIEW mode
   * Uses templateData from parent if available (single fetch optimization)
   */
  async loadTemplateData() {
    this.apiLoading = true;

    try {
      // Use templateData from parent if available, otherwise fetch (fallback for tests)
      const template = this.templateData || await getRequest(`${Apis.GET_WORKFLOW_TEMPLATE}/${this.workflowTemplateId}`);

      if (template && !template?.details?.length) {

        // Extract workflow info from flat fields on template entity
        this.workflowName = template.workflowName || "";
        // Normalize transferType to capitalized form (API may return "push"/"pull" lowercase)
        const rawTransferType = template.transferType || "";
        this.transferType = rawTransferType.charAt(0).toUpperCase() + rawTransferType.slice(1).toLowerCase();
        if (stringComparison(this.transferType, "pull")) {
          this.sourceLocationPath = "";
          this.isSourceLocationPathValid = true;
          this.sourceLocationPathError = "";
        } else {
          this.sourceLocationPath = template.sourceLocationPath || "";
          this.applySourceLocationPathValidation(this.sourceLocationPath);
        }
        this.triggerFilePatterns = template.triggerFilePattern || [];
        this.dataFilePatterns = template.dataFilePattern || [];

        // Save original values for dirty detection
        this.saveOriginalData();

        // Reset dirty state since we just loaded fresh data
        this.isDirty = false;

        // Update validation states
        this.isWorkflowNameValid = this.workflowName.length > 0;
        this.isTransferTypeValid = this.transferType.toLowerCase() === "push" || this.transferType.toLowerCase() === "pull";
      }
    } catch (error) {
      console.error("Failed to load workflow info:", error);
      showToastMessage("Failed to load workflow information.", "danger");
    } finally {
      this.apiLoading = false;
      this.validateForm();
    }
  }

  /**
   * Save current data as original values for dirty detection
   */
  saveOriginalData() {
    this.originalData = {
      workflowName: this.workflowName,
      transferType: this.transferType,
      sourceLocationPath: this.sourceLocationPath,
      triggerFilePatterns: [...this.triggerFilePatterns],
      dataFilePatterns: [...this.dataFilePatterns],
    };
  }

  /**
   * Check if current data differs from original loaded data
   */
  checkDirtyState() {
    const hasChanges =
      this.workflowName !== this.originalData.workflowName ||
      this.transferType !== this.originalData.transferType ||
      this.sourceLocationPath !== this.originalData.sourceLocationPath ||
      JSON.stringify(this.triggerFilePatterns) !== JSON.stringify(this.originalData.triggerFilePatterns) ||
      JSON.stringify(this.dataFilePatterns) !== JSON.stringify(this.originalData.dataFilePatterns);

    this.isDirty = hasChanges;
  }

  /**
   * Mark form as dirty (called when user makes changes)
   * Skips marking dirty during initial load to prevent false positives
   * from @change events firing when inputs are first populated
   */
  markDirty() {
    if (!this.isInitializing) {
      this.isDirty = true;
    }
  }

  /**
   * Validate workflow name
   * Workflow names cannot contain slashes (/ or \) as they become part of S3 paths
   * Template variable placeholders like {workflowname} are allowed
   */
  validateWorkflowName(name: string) {
    this.workflowName = name;

    // Check if name is non-empty
    if (!name || name.trim().length === 0) {
      this.isWorkflowNameValid = false;
      this.workflowNameError = "Workflow Name is required";
      this.markDirty();
      this.validateForm();
      return;
    }

    // Remove {variableName} template placeholders before checking for slashes
    const nameWithoutVariables = name.replace(/\{[a-zA-Z0-9][a-zA-Z0-9_\-]*\}/g, "");

    // Check for slashes in the non-variable parts
    if (nameWithoutVariables.includes('/') || nameWithoutVariables.includes('\\')) {
      this.isWorkflowNameValid = false;
      this.workflowNameError = "Workflow name cannot contain slashes (/ or \\)";
      this.markDirty();
      this.validateForm();
      return;
    }

    this.isWorkflowNameValid = true;
    this.workflowNameError = "";
    this.markDirty();
    this.validateForm();
  }

  /**
   * Validate transfer type
   * Accepts "Push", "Pull" case-insensitively (API may return lowercase)
   */
  validateTransferType(type: string | null | undefined) {
    this.transferType = type || "";
    const normalizedType = (type || "").toLowerCase().trim();
    this.isTransferTypeValid = normalizedType === "push" || normalizedType === "pull";
    if (stringComparison(this.transferType, "pull")) {
      this.sourceLocationPath = "";
      this.isSourceLocationPathValid = true;
      this.sourceLocationPathError = "";
    } else if (normalizedType === "push") {
      this.applySourceLocationPathValidation(this.sourceLocationPath);
    }
    this.markDirty();
    this.validateForm();
  }

  /**
   * Leading/trailing slash rules match WorkflowGeneralInfoView.onSLPchange (ACME-1698).
   * Charset rules are intentionally not applied — templates allow {variable} placeholders.
   */
  private evaluateSourceLocationPath(path: string | null | undefined): { valid: boolean; error: string } {
    const value = path ?? "";
    if (!value || value.trim().length === 0) {
      return { valid: false, error: "Source Location Path is required" };
    }
    if (!value.startsWith("/")) {
      return {
        valid: false,
        error: "Please enter a valid path. It must start with /. Example: /Folder/Path/",
      };
    }
    if (!value.endsWith("/")) {
      return {
        valid: false,
        error: "Please enter a valid path. It must end with /. Example: /Folder/Path/",
      };
    }
    return { valid: true, error: "" };
  }

  private applySourceLocationPathValidation(path: string | null | undefined) {
    const result = this.evaluateSourceLocationPath(path);
    this.isSourceLocationPathValid = result.valid;
    this.sourceLocationPathError = result.error;
  }

  /**
   * Validate source location path
   */
  validateSourceLocationPath(path: string) {
    this.sourceLocationPath = path;
    this.applySourceLocationPathValidation(path);
    this.markDirty();
    this.validateForm();
  }

  /**
   * Handle file pattern changes from FilePatternView
   * FilePatternView dispatches formData with triggerFilePattern and dataFilePattern arrays
   * Note: FilePatternView dispatches on every render, so we must check if data actually changed
   */
  onFilePatternChange(event: CustomEvent) {
    const detail = event.detail;
    let dataChanged = false;

    // FilePatternView sends triggerFilePattern and dataFilePattern arrays directly
    if (detail.triggerFilePattern !== undefined) {
      const newTrigger = detail.triggerFilePattern || [];
      if (JSON.stringify(this.triggerFilePatterns) !== JSON.stringify(newTrigger)) {
        this.triggerFilePatterns = newTrigger;
        dataChanged = true;
      }
    }
    if (detail.dataFilePattern !== undefined) {
      const newData = detail.dataFilePattern || [];
      if (JSON.stringify(this.dataFilePatterns) !== JSON.stringify(newData)) {
        this.dataFilePatterns = newData;
        dataChanged = true;
      }
    }

    // Track midEdit state from file pattern inputs
    if (detail.midEdit !== undefined) {
      this.filePatternMidEdit = detail.midEdit;
    }

    // Only mark dirty if data actually changed
    if (dataChanged) {
      this.markDirty();
    }
    this.validateForm();
  }

  /**
   * Validate entire form and emit validation state
   * Requires: Workflow Name, Transfer Type, Source Location Path (Push only),
   * at least one Data File Pattern, and at least one Trigger File Pattern
   */
  validateForm() {
    const hasDataFilePatterns = this.dataFilePatterns.length > 0;
    const hasTriggerFilePatterns = this.triggerFilePatterns.length > 0;
    const sourceLocationOk =
      stringComparison(this.transferType, "pull") || this.isSourceLocationPathValid;

    const isValid =
      this.isWorkflowNameValid &&
      this.isTransferTypeValid &&
      sourceLocationOk &&
      hasDataFilePatterns &&
      hasTriggerFilePatterns;

    this.emitValidation(isValid);
  }

  /**
   * Emits a "validation-change" event to notify parent components about the validation
   * and dirty status of this tab.
   * Also includes transferType so parent can pass it to Steps/Scheduler tabs.
   * @param valid - Whether the current form/tab is valid or not.
   */
  emitValidation(valid: boolean) {
    this.dispatchEvent(
      new CustomEvent("validation-change", {
        detail: {
          tabName: "workflowInfo",
          valid,
          dirty: this.isDirty,
          midEdit: this.filePatternMidEdit,
          transferType: this.transferType, // Pass to parent for Steps/Scheduler tabs
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get all form data for saving
   */
  getFormData() {
    // Normalize transferType to "Push" or "Pull" format for saving
    const normalizedTransferType = this.transferType
      ? this.transferType.charAt(0).toUpperCase() + this.transferType.slice(1).toLowerCase()
      : "";
    return {
      workflowName: this.workflowName,
      transferType: normalizedTransferType,
      sourceLocationPath: stringComparison(this.transferType, "pull") ? "" : this.sourceLocationPath,
      triggerFilePatterns: this.triggerFilePatterns,
      dataFilePatterns: this.dataFilePatterns,
    };
  }

  /**
   * Get formatted variable options for TemplateStringInput (sorted alphabetically)
   */
  get variableOptions() {
    return (this.variables ?? [])
      .map((v) => ({
        name: v.name,
        value: v.name,
        description: v.description,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  /**
   * Handle new variable added from TemplateStringInput component
   * Propagates the event to the parent component so it can add to the Settings tab
   */
  onVariableAdded(e: CustomEvent) {
    // Stop the original event from bubbling further to prevent double-handling
    e.stopPropagation();

    // Re-dispatch the event to parent (WorkflowTemplateGeneralView)
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    const isViewMode = this.mode === "VIEW";
    const transferTypeOptions = [
      { value: "Push", name: "Push" },
      { value: "Pull", name: "Pull" },
    ];

    return html`
      <div>
        <!-- General Information Section -->
        <h2>General Information</h2>
        <div id="basicInfoContainer">
          <!-- Workflow Name -->
          <jha-tooltip bottom text="Enter Workflow Name. Use '{' for variables. Cannot contain slashes. Max length 64 characters.">
            <template-string-input label="Workflow Name" placeholder="Enter Workflow Name" .required=${true}
              data-auto-id="workflowNameInput" maxlength="64" .maxlength=${64}
              .disabled=${isViewMode} .value=${this.workflowName} .options=${this.variableOptions}
              variableButtonTooltipPosition="top-left" @change=${(e: CustomEvent) => this.validateWorkflowName(e.detail.value)}
              @variable-added=${this.onVariableAdded}
              .errorText=${this.workflowNameError}
              .forceShowWarning=${!this.isWorkflowNameValid && !!this.workflowNameError}>
            </template-string-input>
          </jha-tooltip>
      
          <!-- Transfer Type -->
          <jha-tooltip bottom text="Pull is downloaded to source location. Push is uploaded from source location.">
            <jha-form-select outline label="Transfer Type" .options=${transferTypeOptions} lableKey="name" valueKey="value"
              required warning="Transfer Type is required" ?disabled=${isViewMode} data-auto-id="transferTypeSelect"
              .value=${this.transferType} @change=${(e: Event) => this.validateTransferType((e.target as any).value)}
              emptyOptionLabel="Select type">
            </jha-form-select>
          </jha-tooltip>
        </div>
      
        <!-- Source Location Section (Push only — same as workflow-general-info) -->
        ${!stringComparison(this.transferType, "pull")
        ? html`
        <div id="sourceLocationSection">
          <h2>Source Location</h2>
          <jha-tooltip bottom-right text="Include forward slash ('/') at beginning of path and forward slash ('/') at end of path. Use '{' for variables. Max length of 256 characters.">
            <template-string-input class="full-width" label="Source Location Path" placeholder="Enter Source Location Path"
              data-auto-id="sourceLocationPathInput" maxlength="256" .maxlength=${256}
              .required=${true} .disabled=${isViewMode} .value=${this.sourceLocationPath} .options=${this.variableOptions}
              variableButtonTooltipPosition="top-left" @change=${(e: CustomEvent) =>
            this.validateSourceLocationPath(e.detail.value)}
              @variable-added=${this.onVariableAdded}
              .errorText=${this.sourceLocationPathError}
              .forceShowWarning=${!this.isSourceLocationPathValid && !!this.sourceLocationPathError}>
            </template-string-input>
          </jha-tooltip>
        </div>`
        : undefined}
      
        <!-- File Patterns Section -->
        <div id="filePatternsSection">
          <workflow-template-file-pattern-view data-auto-id="filePatternView" .formData=${{
        triggerFilePattern: this.triggerFilePatterns,
        dataFilePattern: this.dataFilePatterns,
        valid: true,
        isEdit: !isViewMode,
      }} .variables=${this.variables} .isReadOnly=${isViewMode} @onFilePatternChange=${this.onFilePatternChange}
            @variable-added=${this.onVariableAdded}>
          </workflow-template-file-pattern-view>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-info-tab": WorkflowTemplateInfoTab;
  }
}
